/**
 * Site-wide video popup.
 *
 * Any element carrying a [data-video-modal] attribute opens a modal player.
 * The video source is the attribute value, or the element's href when the
 * attribute is empty (so links degrade to plain navigation without JS).
 *
 * Supported sources:
 *   - YouTube: watch?v=, youtu.be/, /shorts/, /embed/, /live/  → youtube-nocookie embed
 *   - Vimeo:   vimeo.com/ID, player.vimeo.com/video/ID          → player.vimeo.com embed
 *   - Direct files: .mp4 / .webm / .ogg / .mov                  → native <video>
 *
 * The dialog is built lazily on first use; the player is injected on open
 * and removed on close so playback always stops. Focus is trapped while
 * open, Escape / backdrop / close button dismiss, and focus returns to the
 * trigger afterwards.
 *
 * Also exposed as window.PacificVideoModal = { open(url, opts), close() }
 * so other modules (welcome-modal.js) can hand off to it.
 *
 * Language variants: Easyling serves /es/ and /pt/ by translating the English
 * page, so the server always renders the English video URL. A trigger may
 * carry data-video-url-es / data-video-url-pt; when the first path segment is
 * es or pt, that URL replaces the English one (swapped in after page load, and
 * resolved again on click in case the click lands before load). No variant →
 * the English URL plays.
 *
 * Styles: assets/css/components/video-modal.css
 */

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, video';
const VIDEO_LANGS = ['es', 'pt'];

let root = null;
let player = null;
let closeBtn = null;
let lastFocus = null;
let isOpen = false;

/**
 * Turn a public video URL into an embeddable source.
 * @param {string} url
 * @returns {{type: 'iframe'|'video', src: string}|null}
 */
function parseSource(url) {
    let u;
    try {
        u = new URL(url, window.location.href);
    } catch (e) {
        return null;
    }
    const host = u.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
        const id = u.pathname.slice(1).split('/')[0];
        return id ? youtubeSource(id, u) : null;
    }

    if (/(^|\.)youtube(-nocookie)?\.com$/.test(host)) {
        let id = u.searchParams.get('v');
        if (!id) {
            const m = u.pathname.match(/\/(embed|shorts|live|v)\/([\w-]{6,})/);
            if (m) id = m[2];
        }
        return id ? youtubeSource(id, u) : null;
    }

    if (/(^|\.)vimeo\.com$/.test(host)) {
        const m = u.pathname.match(/\/(?:video\/)?(\d+)/);
        if (!m) return null;
        const hash = u.searchParams.get('h');
        const params = new URLSearchParams({ autoplay: '1', dnt: '1' });
        if (hash) params.set('h', hash);
        return { type: 'iframe', src: `https://player.vimeo.com/video/${m[1]}?${params}` };
    }

    if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(u.pathname)) {
        return { type: 'video', src: u.href };
    }

    // Unknown host: assume it is already an embeddable player URL.
    return { type: 'iframe', src: u.href };
}

function youtubeSource(id, u) {
    const params = new URLSearchParams({
        autoplay: '1',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
    });
    const start = u.searchParams.get('t') || u.searchParams.get('start');
    if (start) params.set('start', String(parseInt(start, 10) || 0));
    return { type: 'iframe', src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${params}` };
}

function build() {
    root = document.createElement('div');
    root.className = 'video-modal';
    root.hidden = true;
    root.innerHTML = `
        <div class="video-modal__backdrop" data-video-modal-close></div>
        <div class="video-modal__dialog" role="dialog" aria-modal="true" aria-label="Video player" tabindex="-1">
            <button type="button" class="video-modal__close" data-video-modal-close aria-label="Close video">
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            <div class="video-modal__player"></div>
            <p class="video-modal__fdic" style="color:#AAAAAA;font-family:'Poppins',sans-serif;font-size:12px;font-weight:400;margin:12px 0 0;text-align:center;">Member FDIC</p>
        </div>`;
    document.body.appendChild(root);

    player = root.querySelector('.video-modal__player');
    closeBtn = root.querySelector('.video-modal__close');

    root.addEventListener('click', (e) => {
        if (e.target.closest('[data-video-modal-close]')) close();
    });
    root.addEventListener('keydown', onKeydown);
}

function onKeydown(e) {
    if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
    }
    if (e.key !== 'Tab') return;

    const items = [...root.querySelectorAll(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}

/**
 * Open the player.
 * @param {string} url            Public video URL (see parseSource).
 * @param {{trigger?: Element, title?: string}} [opts]
 * @returns {boolean} false when the URL could not be turned into a player.
 */
export function open(url, opts = {}) {
    const source = parseSource(url);
    if (!source) return false;
    if (!root) build();

    lastFocus = opts.trigger || document.activeElement;
    player.innerHTML = '';

    if (source.type === 'video') {
        const video = document.createElement('video');
        video.src = source.src;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        player.appendChild(video);
    } else {
        const frame = document.createElement('iframe');
        frame.src = source.src;
        frame.title = opts.title || 'Video player';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        player.appendChild(frame);
    }

    // Swap disclosure text for translated pages.
    const fdic = root.querySelector('.video-modal__fdic');
    if (fdic && opts.trigger) {
        const lang = pageLang();
        const localized = lang ? opts.trigger.getAttribute(`data-disclosure-${lang}`) || '' : '';
        fdic.textContent = localized || 'Member FDIC';
    }

    root.hidden = false;
    document.body.classList.add('video-modal-open');
    requestAnimationFrame(() => root.classList.add('is-open'));
    isOpen = true;
    closeBtn.focus();

    // Let other overlays (welcome-modal.js) step aside.
    document.dispatchEvent(new CustomEvent('pacific:video-modal-open', { detail: { url } }));
    return true;
}

export function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    document.body.classList.remove('video-modal-open');
    player.innerHTML = ''; // removing the iframe/video stops playback
    root.hidden = true;

    if (lastFocus && document.contains(lastFocus) && lastFocus.offsetParent !== null) {
        lastFocus.focus();
    }
    lastFocus = null;
}

/** 'es' / 'pt' when the first path segment is a translated language, else ''. */
function pageLang() {
    const segment = window.location.pathname.split('/')[1] || '';
    return VIDEO_LANGS.includes(segment) ? segment : '';
}

/** The trigger's video URL for the current language, or '' to keep English. */
function localizedUrl(trigger) {
    const lang = pageLang();
    return lang ? trigger.getAttribute(`data-video-url-${lang}`) || '' : '';
}

/** Point every trigger (href included, for long-press / copy link) at its localized video. */
function localizeTriggers() {
    if (!pageLang()) return;
    document.querySelectorAll('[data-video-modal]').forEach((trigger) => {
        const url = localizedUrl(trigger);
        if (!url) return;
        trigger.setAttribute('data-video-modal', url);
        if (trigger.hasAttribute('href')) trigger.setAttribute('href', url);
    });
}

export function init() {
    if (document.readyState === 'complete') {
        localizeTriggers();
    } else {
        window.addEventListener('load', localizeTriggers, { once: true });
    }

    // Delegated (works for late-rendered markup) and in the CAPTURE phase so the
    // click never reaches per-link handlers such as the external-link warning in
    // footer-general.php — a YouTube href would otherwise trigger that popup too.
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-video-modal]');
        if (!trigger) return;
        const url = localizedUrl(trigger) || trigger.getAttribute('data-video-modal') || trigger.getAttribute('href') || '';
        if (!url) return;
        const title = trigger.getAttribute('data-video-title') || trigger.textContent.trim() || undefined;
        if (open(url, { trigger, title })) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, true);

    window.PacificVideoModal = { open, close };
}
